import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Moon, Sun, LogOut, User, Upload, Settings, Bookmark } from 'lucide-react';
import { Button } from './ui/button';
import { PelicanLogo } from './PelicanLogo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const isAdmin = user && user.role === 'admin';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 w-full backdrop-blur-xl bg-white/80 dark:bg-slate-900/80 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <PelicanLogo className="h-8 w-8 text-primary" strokeWidth={1.5} />
            <span className="text-xl font-semibold tracking-tight">ArdNotes</span>
          </Link>

          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/subjects" data-testid="nav-subjects">
              <Button variant="ghost" size="sm">Subjects</Button>
            </Link>

            <Link to="/saved" data-testid="nav-saved" className="hidden sm:inline-block">
              <Button variant="ghost" size="sm">
                <Bookmark className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                Saved
              </Button>
            </Link>

            <Link to="/upload" data-testid="nav-upload">
              <Button size="sm" className="gap-2">
                <Upload className="h-4 w-4" strokeWidth={2} />
                Upload
              </Button>
            </Link>

            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
            >
              {theme === 'light' ? <Moon className="h-5 w-5" strokeWidth={1.5} /> : <Sun className="h-5 w-5" strokeWidth={1.5} />}
            </Button>

            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="user-menu">
                    <User className="h-5 w-5" strokeWidth={1.5} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                    <Settings className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    Admin Panel
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};
